import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { MedplumClient } from '@medplum/core';
import { jwtDecode } from 'jwt-decode';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Session from 'supertokens-auth-react/recipe/session';

import { useApiClient } from '@eluve/api-client-provider';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';
import { useIsFeatureFlagEnabled } from '@eluve/smart-blocks';

import { createFhirApolloClient } from './fhirApolloClient';

/**
 * Consider the Medplum access token "about to expire" once it has this much
 * or less remaining lifetime. Checked inline on every FHIR request — a
 * setTimeout-based scheme is unreliable because backgrounded tabs throttle
 * timers (Chrome caps to >1min, can freeze entirely) and laptop sleep can
 * delay them by hours.
 */
const TOKEN_REFRESH_LEAD_MS = 60_000;

export interface MedplumAccessSummary {
  level: 'broad' | 'scoped-gp';
  reason: 'eluve-admin' | 'tenant-admin' | 'tenant-member';
}

interface MedplumContextValue {
  /** True when the tenant has the MEDPLUM flag on AND a Medplum project exists. */
  enabled: boolean;
  /** Authenticated, project-scoped client. Null until login completes. */
  client: MedplumClient | null;
  /**
   * Apollo client pointed at the tenant-scoped FHIR GraphQL endpoint. Null
   * until the MedplumClient has finished the token exchange. Use this for
   * Patient / Practitioner / resource queries; fall back to `client` (REST)
   * for operations the FHIR GraphQL spec does not cover (e.g. $history).
   */
  fhirClient: ApolloClient<NormalizedCacheObject> | null;
  /** FHIR Practitioner ID for the current user in this tenant's project. */
  practitionerId: string | null;
  /**
   * Summary of the AccessPolicy the server attached to this user's
   * ProjectMembership during the most recent setup. Null until setup
   * completes.
   */
  access: MedplumAccessSummary | null;
}

const MedplumContext = createContext<MedplumContextValue>({
  enabled: false,
  client: null,
  fhirClient: null,
  practitionerId: null,
  access: null,
});

export const useMedplum = () => useContext(MedplumContext);

interface MedplumProviderProps {
  /** Medplum server URL (e.g. appConfig.VITE_MEDPLUM_BASE_URL). */
  baseUrl?: string;
  children: React.ReactNode;
}

export const MedplumProvider: React.FC<MedplumProviderProps> = ({
  baseUrl,
  children,
}) => {
  const tenantId = useAssignedTenantIdFromParams();
  const apiClient = useApiClient();
  const flagEnabled = useIsFeatureFlagEnabled('MEDPLUM');

  const [client, setClient] = useState<MedplumClient | null>(null);
  const [fhirClient, setFhirClient] =
    useState<ApolloClient<NormalizedCacheObject> | null>(null);
  const [practitionerId, setPractitionerId] = useState<string | null>(null);
  const [access, setAccess] = useState<MedplumAccessSummary | null>(null);
  const [projectEnabled, setProjectEnabled] = useState(false);
  // Bumping this re-runs the init effect. Used as a last-resort fallback
  // when the FHIR server returns 401 despite an unexpired-looking JWT (e.g.
  // server-side revocation, key rotation); day-to-day expiry is handled
  // inline in the auth link.
  const [reinitCounter, setReinitCounter] = useState(0);

  useEffect(() => {
    if (!flagEnabled || !tenantId || !baseUrl) {
      setClient(null);
      setFhirClient(null);
      setProjectEnabled(false);
      setPractitionerId(null);
      setAccess(null);
      return;
    }

    let cancelled = false;

    const clearState = () => {
      setClient(null);
      setFhirClient(null);
      setPractitionerId(null);
      setAccess(null);
      setProjectEnabled(false);
    };

    const init = async () => {
      const setup = await apiClient.medplum.setup({
        params: { tenantId },
        body: {},
      });
      if (cancelled) return;
      if (setup.status !== 200 || !('clientId' in setup.body)) {
        clearState();
        return;
      }

      const { clientId, practitionerId: pid, access: nextAccess } = setup.body;

      const medplum = new MedplumClient({ baseUrl });

      const exchangeWithSuperTokens = async () => {
        // Force a SuperTokens refresh before the token exchange so we hand
        // Medplum a JWT signed with the current dynamic key. `getAccessToken()`
        // returns whatever's in the cookie without refreshing, which means a
        // long-idle tab can hold a JWT whose `kid` has rotated out of JWKS —
        // Medplum then 400s because our userinfo endpoint can't verify it.
        await Session.attemptRefreshingSession();
        const jwt = await Session.getAccessToken();
        if (!jwt) throw new Error('No SuperTokens access token');
        await medplum.exchangeExternalAccessToken(jwt, clientId);
      };

      await exchangeWithSuperTokens();
      if (cancelled) return;

      // Dedupe concurrent refreshes — multiple in-flight FHIR requests all
      // calling ensureFreshToken would otherwise trigger parallel exchanges.
      let refreshInFlight: Promise<void> | null = null;
      const ensureFreshToken = async () => {
        const token = medplum.getAccessToken();
        if (!token) return;
        let exp: number | undefined;
        try {
          exp = jwtDecode<{ exp?: number }>(token).exp;
        } catch {
          return;
        }
        if (!exp) return;
        const msUntilExpiry = exp * 1000 - Date.now();
        if (msUntilExpiry > TOKEN_REFRESH_LEAD_MS) return;

        if (!refreshInFlight) {
          refreshInFlight = exchangeWithSuperTokens().finally(() => {
            refreshInFlight = null;
          });
        }
        await refreshInFlight;
      };

      const apollo = createFhirApolloClient({
        baseUrl,
        getAccessToken: () => medplum.getAccessToken(),
        ensureFreshToken,
        onUnauthorized: () => {
          if (!cancelled) setReinitCounter((c) => c + 1);
        },
      });

      setClient(medplum);
      setFhirClient(apollo);
      setPractitionerId(pid ?? null);
      setAccess(nextAccess ?? null);
      setProjectEnabled(true);
    };

    init().catch(() => {
      if (!cancelled) clearState();
    });

    return () => {
      cancelled = true;
    };
  }, [apiClient, flagEnabled, tenantId, baseUrl, reinitCounter]);

  const value = useMemo(
    () => ({
      enabled: projectEnabled,
      client,
      fhirClient,
      practitionerId,
      access,
    }),
    [projectEnabled, client, fhirClient, practitionerId, access],
  );

  return (
    <MedplumContext.Provider value={value}>{children}</MedplumContext.Provider>
  );
};
